'use client';

import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RefreshCw, Copy, CheckCircle, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useSessionClaims } from '@/hooks/use-session-claims';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useApplePayDomainsList,
  useApplePayRegisterDomain,
  useApplePayDeregisterDomain,
  useApplePayRegisterCustomDomain,
} from '@/lib/api/apple-pay-domains';

function normalizeDomainForDisplay(domain: string): string {
  let d = domain.toLowerCase().trim();
  if (d.startsWith('www.')) d = d.slice(4);
  return d;
}

function DomainSection({
  title,
  description,
  domains,
  onDeregister,
  isLoading,
  deregisterPending,
  showDeregister = true,
  showRegister = false,
  onRegister,
  registerPending = false,
  domainIntegrationInfo,
}: {
  title: string;
  description: string;
  domains: string[];
  onDeregister: (domain: string) => void;
  isLoading: boolean;
  deregisterPending: boolean;
  showDeregister?: boolean;
  showRegister?: boolean;
  onRegister?: (domain: string) => void;
  registerPending?: boolean;
  domainIntegrationInfo?: Record<string, { storeId: string; accountId: string }[]>;
}) {
  const { t } = useTranslation('manageApplePay');
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (domains.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">{t('byBt.table.empty')}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('byBt.table.domain')}</TableHead>
              {domainIntegrationInfo && (
                <>
                  <TableHead className="text-muted-foreground font-normal">
                    {t('byBt.table.storeId')}
                  </TableHead>
                  <TableHead className="text-muted-foreground font-normal">
                    {t('byBt.table.accountId')}
                  </TableHead>
                </>
              )}
              {(showDeregister || showRegister) && (
                <TableHead className="w-[120px] text-right">{t('byBt.table.action')}</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {domains.map((domain) => {
              const infos = domainIntegrationInfo?.[normalizeDomainForDisplay(domain)];
              return (
                <TableRow key={domain}>
                  <TableCell className="font-mono text-sm">{domain}</TableCell>
                  {domainIntegrationInfo && (
                    <>
                      <TableCell className="text-muted-foreground font-mono text-xs">
                        {infos?.length ? infos.map((i) => i.storeId).join(', ') : '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">
                        {infos?.length ? infos.map((i) => i.accountId).join(', ') : '—'}
                      </TableCell>
                    </>
                  )}
                  {(showDeregister || showRegister) && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {showRegister && onRegister && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => onRegister(domain)}
                            disabled={registerPending}
                          >
                            {registerPending ? t('byBt.table.registering') : t('byBt.table.register')}
                          </Button>
                        )}
                        {showDeregister && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => onDeregister(domain)}
                            disabled={deregisterPending}
                          >
                            {t('byBt.table.deregister')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

const DOMAIN_ASSOCIATION_CONTENT =
  '7b2276657273696f6e223a312c227073704964223a2232354445443144354433384346464645373738354331313243313639413637313730383836424639434435383145374642383642464532393430373337443931222c22637265617465644f6e223a313733383737373130313432347d';

function RegisterCustomDomainSection() {
  const { t } = useTranslation('manageApplePay');
  const [domain, setDomain] = React.useState('');
  const [storeId, setStoreId] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const registerCustom = useApplePayRegisterCustomDomain();

  const handleCopy = () => {
    navigator.clipboard.writeText(DOMAIN_ASSOCIATION_CONTENT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegister = () => {
    const trimmed = domain.trim();
    if (!trimmed) return;
    registerCustom.mutate(
      { domain: trimmed, storeId: storeId.trim() || undefined },
      {
        onSuccess: () => {
          setDomain('');
          setStoreId('');
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('byBt.register.title')}</CardTitle>
        <CardDescription>{t('byBt.register.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-md border bg-muted/40 p-4 space-y-3">
          <p className="text-sm font-medium">{t('byBt.register.step1Title')}</p>
          <p className="text-muted-foreground text-sm">{t('byBt.register.step1Body')}</p>
          <code className="block text-xs font-mono bg-background border rounded px-3 py-2">
            {'https://<your-domain>/.well-known/apple-developer-merchantid-domain-association'}
          </code>
          <div className="relative">
            <pre className="text-xs font-mono bg-background border rounded px-3 py-2 pr-10 break-all whitespace-pre-wrap">
              {DOMAIN_ASSOCIATION_CONTENT}
            </pre>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-1 right-1 h-7 w-7"
              onClick={handleCopy}
              title={t('byBt.register.copy')}
            >
              {copied
                ? <CheckCircle className="h-4 w-4 text-green-500" />
                : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            <Trans
              i18nKey="byBt.register.contentTypeNote"
              ns="manageApplePay"
              components={{ code: <code /> }}
            />
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">{t('byBt.register.step2Title')}</p>
          <p className="text-muted-foreground text-sm">{t('byBt.register.step2Body')}</p>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
              className="max-w-sm font-mono text-sm"
            />
            <Input
              placeholder={t('byBt.register.storeIdPlaceholder')}
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
              className="max-w-xs font-mono text-sm"
            />
            <Button
              onClick={handleRegister}
              disabled={!domain.trim() || registerCustom.isPending}
            >
              {registerCustom.isPending ? t('byBt.table.registering') : t('byBt.table.register')}
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">{t('byBt.register.storeIdHint')}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ApplePayDomainsPage() {
  const { t } = useTranslation('manageApplePay');
  const { isSuperAdmin, isAccountManager, whitelabelScope } = useSessionClaims();
  // The list spans every partner's domains, so a whitelabel-scoped staff user
  // is excluded even with the accountmanager role (mirrors
  // `canViewGlobalApplePayDomains` on the server — the tRPC procedure returns
  // an empty categorization for them regardless).
  const canView = isSuperAdmin || (isAccountManager && !whitelabelScope);
  const { data, isLoading, error, refetch } = useApplePayDomainsList({ enabled: canView });
  const registerMutation = useApplePayRegisterDomain();
  const deregisterMutation = useApplePayDeregisterDomain();
  const [deregisterTarget, setDeregisterTarget] = React.useState<string | null>(null);

  if (!canView) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>{t('common:accountManager.applePayDomainsTab.contactTitle')}</AlertTitle>
        <AlertDescription>{t('common:accountManager.applePayDomainsTab.contactDescription')}</AlertDescription>
      </Alert>
    );
  }

  const handleDeregisterConfirm = () => {
    if (!deregisterTarget) return;
    deregisterMutation.mutate(
      { domain: deregisterTarget },
      { onSuccess: () => setDeregisterTarget(null) }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-muted-foreground text-sm">{t('byBt.intro')}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          {t('byBt.refresh')}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>{t('byBt.error')}</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {/* Summary */}
      {!isLoading && data && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('byBt.summary.btTotal')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.btDomains.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('byBt.summary.matchedEnabled')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.matchedEnabled.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('byBt.summary.matchedDisabled')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.matchedDisabled.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('byBt.summary.orphanedInBt')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.orphanedInBt.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('byBt.summary.dbOnly')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{data.dbOnly.length}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Domain sections */}
      {data && (
        <div className="space-y-4">
          <DomainSection
            title={t('byBt.sections.matchedEnabled.title')}
            description={t('byBt.sections.matchedEnabled.description')}
            domains={data.matchedEnabled}
            onDeregister={setDeregisterTarget}
            isLoading={isLoading}
            deregisterPending={deregisterMutation.isPending}
            domainIntegrationInfo={data.domainIntegrationInfo}
          />
          <DomainSection
            title={t('byBt.sections.matchedDisabled.title')}
            description={t('byBt.sections.matchedDisabled.description')}
            domains={data.matchedDisabled}
            onDeregister={setDeregisterTarget}
            isLoading={isLoading}
            deregisterPending={deregisterMutation.isPending}
            domainIntegrationInfo={data.domainIntegrationInfo}
          />
          <DomainSection
            title={t('byBt.sections.orphanedInBt.title')}
            description={t('byBt.sections.orphanedInBt.description')}
            domains={data.orphanedInBt}
            onDeregister={setDeregisterTarget}
            isLoading={isLoading}
            deregisterPending={deregisterMutation.isPending}
          />
          <DomainSection
            title={t('byBt.sections.dbOnly.title')}
            description={t('byBt.sections.dbOnly.description')}
            domains={data.dbOnly}
            onDeregister={setDeregisterTarget}
            isLoading={isLoading}
            deregisterPending={deregisterMutation.isPending}
            showDeregister={false}
            showRegister={true}
            onRegister={(domain) => registerMutation.mutate({ domain })}
            registerPending={registerMutation.isPending}
            domainIntegrationInfo={data.domainIntegrationInfo}
          />
        </div>
      )}

      {isLoading && !data && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      <RegisterCustomDomainSection />

      <AlertDialog open={!!deregisterTarget} onOpenChange={(open) => !open && setDeregisterTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('byBt.removeDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('byBt.removeDialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeregisterTarget(null)}>
              {t('byBt.removeDialog.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeregisterConfirm}
              disabled={deregisterMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deregisterMutation.isPending
                ? t('byBt.removeDialog.removing')
                : t('byBt.removeDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
